import React, { useRef } from 'react';

import { useCallbackOne } from 'use-memo-one';

import type { ForgeAppError } from '../bridge/ForgeAppError';
import type { OverlayController } from '../bridge/types';
import { ForgeAppErrorMessage } from '../renderer/errorMessages';
import type { Extension } from '../web-client';

type UseRuntimeErrorOverlayInput = {
	extension: Extension;
	/**
	 * Product `onError` callback, called with the error's `errorType` when the overlay shows.
	 * The overlay owns display; this is notification only, so product side-effects still run
	 * (UIKit: `onError`/`onAppError` → host loader teardown, SLO tracking). No-op when absent.
	 */
	onError?: (err: string | undefined) => void;
	overlayController: OverlayController;
	/**
	 * UIKit only — suppresses the host `RendererNext` while the error shows so the app
	 * and the error don't display at once. `undefined`/no-op for Custom UI (the iframe
	 * is the visible surface).
	 */
	setShouldShowRenderer?: (show: boolean) => void;
};

/**
 * Shared error presenter for the v2 runtimes (iframe + web worker). Returns a stable
 * `showAppError(error)` that renders the Forge app error UI into the single overlay slot
 * and suppresses the host renderer (UIKit) — the one place that bundles overlay +
 * suppression so callers can't half-do it. Takes the whole `ForgeAppError` so future
 * error data reaches the display without changing this signature or the wiring.
 *
 * i18n: the content is rendered **directly** into the overlay (exactly like
 * `ThreeLOPrompt`), so it inherits the host's ancestor `IntlProvider` — which carries
 * the product-loaded locale + message catalog. Do NOT wrap it in a new `IntlProvider`
 * here: a messages-less provider would shadow the ancestor and force English
 * `defaultMessage`s.
 *
 * `affectsLayout=true` releases the container height + hides the iframe so the message
 * is visible even when the iframe is hidden (UIKit). The error is unrecoverable — there is
 * no restore step.
 */
export const useRuntimeErrorOverlay = ({
	overlayController,
	extension,
	setShouldShowRenderer,
	onError,
}: UseRuntimeErrorOverlayInput): ((error: ForgeAppError) => void) => {
	// Always-current values so the stable callback never reads stale props.
	const latestRef = useRef({ overlayController, extension, setShouldShowRenderer, onError });
	latestRef.current = { overlayController, extension, setShouldShowRenderer, onError };

	return useCallbackOne((error: ForgeAppError) => {
		const { overlayController, extension, setShouldShowRenderer, onError } = latestRef.current;
		overlayController.show(
			<ForgeAppErrorMessage extension={extension} error={error.errorType} />,
			true,
		);
		setShouldShowRenderer?.(false);
		// Notify the product. Display is handled above; in UIKit `setShouldShowRenderer(false)` keeps
		// RendererNext suppressed, so this only fires the `onError`/`onAppError` side-effects.
		onError?.(error.errorType);
	}, []);
};
